import { useCallback, useEffect, useRef } from 'react';
import { drainSseBuffer } from '../utils/sseEventParser.js';

export type UseServerSentStreamOptions<T> = {
  url: string | null;
  enabled?: boolean;
  /** Fetch Authorization header value; refreshed on 401 before reconnect. */
  getAccessToken: () => Promise<string | null>;
  parseEvent: (data: string) => T | null;
  onEvent: (event: T) => void;
  onReconnect?: () => void;
  /** Fired when the SSE byte stream is active (`true`) or ends before terminal close (`false`). */
  onStreamConnectedChange?: (connected: boolean) => void;
  reconnectDelayMs?: number;
  maxReconnectDelayMs?: number;
};

const DEFAULT_RECONNECT_MS = 1000;
const MAX_RECONNECT_MS = 30_000;

/**
 * Generic SSE client with exponential backoff reconnect and token refresh on 401.
 */
export function useServerSentStream<T>({
  url,
  enabled = true,
  getAccessToken,
  parseEvent,
  onEvent,
  onReconnect,
  onStreamConnectedChange,
  reconnectDelayMs = DEFAULT_RECONNECT_MS,
  maxReconnectDelayMs = MAX_RECONNECT_MS,
}: UseServerSentStreamOptions<T>): void {
  const onEventRef = useRef(onEvent);
  const onReconnectRef = useRef(onReconnect);
  const onStreamConnectedChangeRef = useRef(onStreamConnectedChange);
  const parseEventRef = useRef(parseEvent);
  const getAccessTokenRef = useRef(getAccessToken);

  useEffect(() => {
    onEventRef.current = onEvent;
    onReconnectRef.current = onReconnect;
    onStreamConnectedChangeRef.current = onStreamConnectedChange;
    parseEventRef.current = parseEvent;
    getAccessTokenRef.current = getAccessToken;
  }, [onEvent, onReconnect, onStreamConnectedChange, parseEvent, getAccessToken]);

  const connectOnce = useCallback(
    async (signal: AbortSignal, attempt: number): Promise<'closed' | 'retry'> => {
      /* v8 ignore next 3 -- useEffect only invokes connectOnce when url is set */
      if (!url) {
        return 'closed';
      }

      let token: string | null = null;
      try {
        token = await getAccessTokenRef.current();
      } catch {
        return 'retry';
      }
      if (signal.aborted) return 'closed';

      let response: Response;
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal,
        });
      } catch (err) {
        if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
          return 'closed';
        }
        onStreamConnectedChangeRef.current?.(false);
        return 'retry';
      }

      if (response.status === 401) {
        await getAccessTokenRef.current();
        return 'retry';
      }

      if (!response.ok || !response.body) {
        onStreamConnectedChangeRef.current?.(false);
        return 'retry';
      }

      onStreamConnectedChangeRef.current?.(true);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (!signal.aborted) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const drained = drainSseBuffer(buffer);
          buffer = drained.remainder;
          for (const raw of drained.payloads) {
            const parsed = parseEventRef.current(raw);
            if (parsed != null) {
              onEventRef.current(parsed);
            }
          }
        }
      } catch (err) {
        if (!signal.aborted) {
          console.warn('[SSE] stream error', err);
        }
      } finally {
        reader.releaseLock();
      }

      onStreamConnectedChangeRef.current?.(false);
      if (attempt > 0) {
        onReconnectRef.current?.();
      }
      return 'retry';
    },
    [url],
  );

  useEffect(() => {
    if (!enabled || !url) {
      return;
    }

    const controller = new AbortController();
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const run = async (): Promise<void> => {
      while (!disposed && !controller.signal.aborted) {
        const result = await connectOnce(controller.signal, attempt);
        if (disposed || controller.signal.aborted || result === 'closed') {
          break;
        }
        attempt += 1;
        const delay = Math.min(
          reconnectDelayMs * 2 ** Math.min(attempt, 6),
          maxReconnectDelayMs,
        );
        await new Promise<void>((resolve) => {
          reconnectTimer = setTimeout(() => resolve(), delay);
        });
      }
    };

    void run();

    return () => {
      disposed = true;
      controller.abort();
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
    };
  }, [url, enabled, connectOnce, reconnectDelayMs, maxReconnectDelayMs]);
}
